import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as auth0 from 'auth0-js';
import { environment } from '../../../environments/environment';
import { AngularFireAuth } from "@angular/fire/auth";
import { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import * as jwt_decode from 'jwt-decode';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

@Injectable()
export class Auth0Service {

  private _idToken: string;
  private _accessToken: string;
  private _expiresAt: number;
  private _tokenType: string;
  private _idTokenPayload: any;
  private _firbaseAuth: AngularFireAuth;

  private cernerSignIn = new BehaviorSubject<any>({})
  public $cernerSignIn = this.cernerSignIn.asObservable();

  auth0 = new auth0.WebAuth(environment.auth0Config);

  constructor(public router: Router,
    public firebaseAuth: AngularFireAuth
    ) {
    let tokenInfo = JSON.parse(localStorage.getItem('tokenInfo')) || {};
    this._idToken = tokenInfo.idToken || '';
    this._accessToken = tokenInfo.accessToken || '';
    this._expiresAt = tokenInfo.expiresAt || 0;
    this._tokenType = tokenInfo.tokenType || '';
    this._firbaseAuth = firebaseAuth;
    this._idTokenPayload = tokenInfo.idTokenPayload || {};
    if(this.isAuthenticated) {
      this._firebaseLogin()
    } else {
      this.logout();
    }
  }

  get accessToken(): string {
    return this._accessToken;
  }

  get idTokenDetail(): {}{
    return this._idTokenPayload;
  }

  get tokenType(): string {
    return this._tokenType;
  }
  
  get idToken(): string {
    return this._idToken;
  }

  public login(): void {
    this.auth0.authorize(this.authConfig());
  }

  private authConfig(){
    let deeplink = JSON.parse(sessionStorage.getItem('deeplink'));
    if(deeplink&&deeplink.params){
      let params = new URLSearchParams(deeplink.params);
      let connection = params.get('connection');
      let email = params.get('email_addr');
      let phone = params.get('phone_number');
      if(connection === 'sms' && phone){
        if(!phone.includes('+'))
          phone = `+${phone}`
        const phoneNumber = parsePhoneNumberFromString(phone);
        return {
          login_hint: phoneNumber.nationalNumber.trim(),
          connectionType: connection
        }
      }else if(email) 
        return {login_hint: email.trim()}
    }
    return {};
  }

  public cerner(): void {
    this.auth0.authorize(environment.auth0Cerner)
  }

  public epic(): void{
    this.auth0.authorize(environment.auth0Epic)
  }

  public handleAuthentication(): void {
    this.auth0.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.localLogin(authResult).then(()=>{
          let deeplink = JSON.parse(sessionStorage.getItem('deeplink'));
          if(deeplink&&deeplink.url){
            let url = deeplink.url;
            sessionStorage.removeItem('deeplink');
            this.router.navigateByUrl(url);
          }else{
            this.router.navigate(['/app/care-rewards']);
          }
        }).catch(()=>{
          this.router.navigate(['']);
        })
      } else if (err) {
        this.router.navigate(['']);
        console.log("error : ", err);
      }
    });
  }

  private _firebaseLogin(){
    return new Promise((resolve, reject)=>{
      const firebaseToken = this._idTokenPayload && this._idTokenPayload[`${environment.auth0Config.audience}firebase_token`]
      if (firebaseToken) {
        try {
          this._firbaseAuth.signInWithCustomToken(firebaseToken).then(result => {
            // firebase auth successful
            if (environment.production) {
              console.log("Firebase Auth successful")
            } else {
              console.log("Firebase Auth successful: ", result)
            }
            resolve(result);
          }).catch(error => {
            console.error("error while signing in with custom token", error)
            reject(error);
          })
        } catch (error) {
          console.error("Error calling auth", error);
          reject(error);
        }
      }
    })
  }

  private localLogin(authResult){
    // Set the time that the Access Token will expire at
    const expiresAt = (authResult.expiresIn * 1000) + Date.now();
    this._accessToken = authResult.accessToken;
    this._idToken = authResult.idToken;
    this._expiresAt = expiresAt;
    this._tokenType = authResult.tokenType;
    var decoded = jwt_decode(authResult.accessToken); 

    authResult.idTokenPayload.patient_id = decoded[environment.patientIdUrl];
    this._idTokenPayload = authResult.idTokenPayload

    let info = { 
      accessToken: this._accessToken, 
      idToken: this._idToken, 
      expiresAt: this._expiresAt,
      tokenType: this._tokenType,
      idTokenPayload: this._idTokenPayload
    };

    localStorage.setItem("tokenInfo", JSON.stringify(info));
    return new Promise((resolve, reject)=>{
      this._firebaseLogin().then(result=>{
        resolve(result)
      }).catch(error=>{
        reject(error)
      })
    })
  }

  public renewTokens(): Observable<string> {
    return Observable.create(observer => {
    this.auth0.checkSession({}, (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        return this.localLogin(authResult).then(res=>{
          observer.next(authResult.accessToken);
          observer.complete();
        })
      } else if (err) {
        this.logout();
      }
    });
  })
}

  public logout(): void {
    // Remove tokens and expiry time
    this._accessToken = '';
    this._idToken = '';
    this._expiresAt = 0;
    this._tokenType = '';
    localStorage.clear();
    sessionStorage.clear();

    this.auth0.logout({
      returnTo: window.location.origin
    }); 
  }

  public isAuthenticated(): boolean {
    // Check whether the current time is past the
    // access token's expiry time
    let date = new Date().getMilliseconds();
    return this._accessToken && (date < this._expiresAt);
  }

  public setClinicalData(data){
    this.cernerSignIn.next(data);
  }

}